import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Printer } from "lucide-react";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { computeTimeRanges, formatTime, totalDuration, CATEGORY_STYLES } from "@/lib/session";

export default function SharedSession() {
  const { token } = useParams();
  const [session, setSession] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    api.get(`/sessions/shared/${token}`).then(({ data }) => setSession(data)).catch(() => setError(true));
  }, [token]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--brand-bg)] dark:bg-[var(--brand-bg-dark)]" data-testid="shared-session-not-found">
        <p className="font-display text-xl italic text-muted-foreground">This shared session link is invalid or has been revoked.</p>
      </div>
    );
  }

  if (!session) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--brand-bg)] dark:bg-[var(--brand-bg-dark)]">
        <p className="font-display italic text-lg text-muted-foreground">Loading session...</p>
      </div>
    );
  }

  const ranges = computeTimeRanges(session.start_time, session.segments);

  return (
    <div className="min-h-screen bg-[var(--brand-bg)] dark:bg-[var(--brand-bg-dark)]">
      <header className="bg-gradient-to-r from-[var(--brand-navy)] to-[var(--brand-navy-hover)] text-amber-50 px-6 py-8 flex items-center justify-between">
        <div>
          <p className="text-xs uppercase tracking-widest text-amber-300/80 font-mono mb-1">View-only shared session</p>
          <h1 className="font-sacred text-3xl">{session.title}</h1>
          <p className="font-display italic text-amber-100/80 mt-1">{session.subtitle}</p>
        </div>
        <Button variant="outline" className="no-print border-amber-200/40 text-amber-50 hover:bg-amber-50/10" onClick={() => window.print()} data-testid="shared-print-button">
          <Printer className="h-4 w-4 mr-2" /> Print
        </Button>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-8 space-y-4">
        <p className="font-mono text-xs px-2.5 py-1 rounded-full bg-amber-100 dark:bg-amber-950/40 text-[var(--brand-gold-text)] dark:text-amber-300 border border-[var(--brand-gold)] w-fit" data-testid="shared-total-duration">
          {totalDuration(session.segments)} min total
        </p>
        {session.segments.map((seg, idx) => {
          const style = CATEGORY_STYLES[seg.category] || CATEGORY_STYLES.Activities;
          return (
            <div key={seg.id} className="bg-white dark:bg-[var(--brand-card-dark)] border border-[var(--brand-border)] dark:border-[var(--brand-border-dark)] rounded-lg p-5" data-testid={`shared-segment-${idx}`}>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <span className={`inline-block text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded-full border mb-1.5 ${style.badge}`}>{seg.category}</span>
                  <h3 className="font-semibold text-base">{seg.title}</h3>
                </div>
                <p className="font-mono text-xs text-muted-foreground shrink-0">{formatTime(ranges[idx].start)} &ndash; {formatTime(ranges[idx].end)}</p>
              </div>
              {seg.body && <p className="text-sm mt-2 whitespace-pre-line">{seg.body}</p>}
            </div>
          );
        })}
      </main>
    </div>
  );
}
